import { gql } from '@apollo/client';
import client from '../apollo-client';
import { AssistantChatInput, AssistantChatResult, AssistantTurn } from '../interfaces/Assistant';

const CHAT_RESULT_FIELDS = `
  sessionId
  assistantMessage
  toolResults { toolName ok output errorMessage }
  pendingAction { id toolName payloadJson previewText riskLevel expiresAt }
  suggestedActions { label prompt }
  touchedResources { kind id op }
`;

const ASSISTANT_EXECUTE_PENDING = gql`
  mutation AssistantExecutePending($pendingId: ID!) {
    assistantExecutePending(pendingId: $pendingId) { ${CHAT_RESULT_FIELDS} }
  }
`;

const ASSISTANT_CANCEL_PENDING = gql`
  mutation AssistantCancelPending($pendingId: ID!) {
    assistantCancelPending(pendingId: $pendingId)
  }
`;

const ASSISTANT_CLEAR_SESSION = gql`
  mutation AssistantClearSession($sessionId: ID!) {
    assistantClearSession(sessionId: $sessionId)
  }
`;

const ASSISTANT_HISTORY = gql`
  query AssistantSessionHistory($sessionId: ID!) {
    assistantSessionHistory(sessionId: $sessionId) {
      role
      content
      toolName
      toolCalls { id name input }
    }
  }
`;

type StreamHandlers = {
  onStatus?: (phase: string, message: string) => void;
  onTool?: (payload: { name: string; label: string; state: 'start' | 'done' | 'error'; detail?: string }) => void;
  onNote?: () => void;
  onDelta?: (text: string) => void;
};

const BACKEND_URI = process.env.VITE_BACKEND_URI || '';

function parseSseBlocks(buffer: string): { events: { event: string; data: string }[]; rest: string } {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() || '';
  const events: { event: string; data: string }[] = [];
  for (const block of parts) {
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length) events.push({ event, data: dataLines.join('\n') });
  }
  return { events, rest };
}

export const assistantService = {
  async chat(input: AssistantChatInput, signal?: AbortSignal, handlers?: StreamHandlers): Promise<AssistantChatResult> {
    const token = localStorage.getItem('token') || '';
    const res = await fetch(`${BACKEND_URI}/assistant/chat/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(input),
      signal,
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `HTTP ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let doneResult: AssistantChatResult | null = null;
    let streamError: string | null = null;

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSseBlocks(buffer);
      buffer = parsed.rest;
      for (const ev of parsed.events) {
        if (ev.event === 'ping') continue;
        let payload: Record<string, unknown> = {};
        try {
          payload = JSON.parse(ev.data) as Record<string, unknown>;
        } catch {
          continue;
        }
        if (ev.event === 'status') handlers?.onStatus?.(String(payload.phase || ''), String(payload.message || ''));
        else if (ev.event === 'tool') {
          handlers?.onTool?.({
            name: String(payload.name || ''),
            label: String(payload.label || payload.name || ''),
            state: (payload.state as 'start' | 'done' | 'error') || 'start',
            detail: payload.detail ? String(payload.detail) : undefined,
          });
        } else if (ev.event === 'note') handlers?.onNote?.();
        else if (ev.event === 'delta') handlers?.onDelta?.(String(payload.text || ''));
        else if (ev.event === 'error') streamError = String(payload.message || 'Eroare la chat.');
        else if (ev.event === 'done') doneResult = payload as unknown as AssistantChatResult;
      }
    }

    if (streamError) throw new Error(streamError);
    if (!doneResult) throw new Error('Răspunsul s-a întrerupt înainte să se termine.');
    return doneResult;
  },

  async executePending(pendingId: string): Promise<AssistantChatResult> {
    const { data } = await client.mutate({ mutation: ASSISTANT_EXECUTE_PENDING, variables: { pendingId }, fetchPolicy: 'no-cache' });
    return data.assistantExecutePending;
  },

  async cancelPending(pendingId: string): Promise<boolean> {
    const { data } = await client.mutate({ mutation: ASSISTANT_CANCEL_PENDING, variables: { pendingId } });
    return data.assistantCancelPending;
  },

  async clearSession(sessionId: string): Promise<boolean> {
    const { data } = await client.mutate({ mutation: ASSISTANT_CLEAR_SESSION, variables: { sessionId } });
    return data.assistantClearSession;
  },

  async getHistory(sessionId: string): Promise<AssistantTurn[]> {
    const { data } = await client.query({ query: ASSISTANT_HISTORY, variables: { sessionId }, fetchPolicy: 'no-cache' });
    return data.assistantSessionHistory;
  },
};
